import { useEffect, useMemo, useRef, useState } from "react";
import { Input } from "@/components/ui/input";

// Dataset minimo di marche comuni in Italia (ordinato per popolarità)
export const COMMON_BRANDS = [
  "Fiat", "Volkswagen", "Ford", "Renault", "Peugeot", "Citroën", "Opel", "Toyota",
  "BMW", "Mercedes-Benz", "Audi", "Hyundai", "Kia", "Nissan", "Jeep", "Lancia",
  "Alfa Romeo", "Suzuki", "Mazda", "Skoda", "SEAT", "MINI", "Volvo", "Tesla",
  "Honda", "Smart", "Lexus", "Dacia", "Porsche", "Land Rover", "Jaguar", "Mitsubishi",
  "Subaru", "DS Automobiles", "Cupra", "Maserati", "Ferrari", "Lamborghini",
];

// Dataset modelli comuni per ciascuna marca (per autocomplete predittivo)
// Si aggiunge dinamicamente con i modelli realmente usati nel DB
export const COMMON_MODELS_BY_BRAND = {
  fiat: ["500", "500L", "500X", "Panda", "Punto", "Tipo", "Bravo", "Croma", "Doblò", "Ducato", "Fiorino", "Multipla", "Qubo", "Stilo", "Uno", "Idea"],
  volkswagen: ["Golf", "Polo", "Tiguan", "Passat", "T-Cross", "T-Roc", "Touareg", "Touran", "Up!", "Caddy", "ID.3", "ID.4", "Arteon", "Sharan"],
  ford: ["Fiesta", "Focus", "Puma", "Kuga", "Ka", "Ka+", "Mondeo", "S-Max", "Galaxy", "EcoSport", "Edge", "Ranger", "Transit"],
  renault: ["Clio", "Captur", "Megane", "Scenic", "Kadjar", "Koleos", "Twingo", "Zoe", "Kangoo", "Trafic", "Master", "Talisman", "Espace"],
  peugeot: ["208", "2008", "308", "3008", "508", "5008", "Partner", "Boxer", "Rifter", "Traveller", "e-208"],
  "alfa romeo": ["Giulia", "Stelvio", "Giulietta", "MiTo", "Tonale", "159", "147", "156", "Brera", "Spider"],
  citroen: ["C1", "C3", "C3 Aircross", "C4", "C4 Cactus", "C5", "C5 Aircross", "Berlingo", "Jumpy", "Jumper", "Picasso"],
  "citroën": ["C1", "C3", "C3 Aircross", "C4", "C4 Cactus", "C5", "C5 Aircross", "Berlingo", "Jumpy", "Jumper", "Picasso"],
  opel: ["Corsa", "Astra", "Mokka", "Crossland", "Grandland", "Insignia", "Combo", "Vivaro", "Movano", "Karl", "Adam", "Zafira"],
  toyota: ["Yaris", "Corolla", "C-HR", "RAV4", "Aygo", "Auris", "Avensis", "Verso", "Land Cruiser", "Hilux", "Proace", "Camry", "Prius"],
  bmw: ["Serie 1", "Serie 2", "Serie 3", "Serie 4", "Serie 5", "Serie 6", "Serie 7", "X1", "X2", "X3", "X4", "X5", "X6", "X7", "Z4", "i3", "i4", "iX"],
  "mercedes-benz": ["Classe A", "Classe B", "Classe C", "Classe E", "Classe S", "CLA", "CLS", "GLA", "GLB", "GLC", "GLE", "GLS", "Vito", "Sprinter", "EQA", "EQB", "EQC"],
  audi: ["A1", "A3", "A4", "A5", "A6", "A7", "A8", "Q2", "Q3", "Q4", "Q5", "Q7", "Q8", "TT", "R8", "e-tron"],
  hyundai: ["i10", "i20", "i30", "Tucson", "Kona", "Bayon", "Santa Fe", "Ioniq", "Ioniq 5", "Ioniq 6"],
  kia: ["Picanto", "Rio", "Stonic", "Ceed", "XCeed", "Sportage", "Sorento", "Niro", "Soul", "EV6"],
  nissan: ["Micra", "Juke", "Qashqai", "X-Trail", "Leaf", "Note", "Pulsar", "Navara", "Townstar"],
  jeep: ["Renegade", "Compass", "Cherokee", "Grand Cherokee", "Wrangler", "Avenger", "Gladiator"],
  lancia: ["Ypsilon", "Delta", "Musa", "Thema", "Voyager"],
  suzuki: ["Swift", "Ignis", "Vitara", "S-Cross", "Jimny", "Baleno", "Across"],
  mazda: ["2", "3", "6", "CX-3", "CX-30", "CX-5", "MX-5", "MX-30"],
  skoda: ["Fabia", "Scala", "Octavia", "Superb", "Kamiq", "Karoq", "Kodiaq", "Enyaq"],
  seat: ["Ibiza", "Leon", "Arona", "Ateca", "Tarraco", "Mii"],
  mini: ["Cooper", "Cooper S", "Countryman", "Clubman", "Cabrio", "Electric"],
  volvo: ["XC40", "XC60", "XC90", "S60", "S90", "V60", "V90", "EX30", "EX90"],
  tesla: ["Model 3", "Model Y", "Model S", "Model X", "Cybertruck"],
  honda: ["Jazz", "Civic", "CR-V", "HR-V", "e:Ny1"],
  smart: ["fortwo", "forfour", "EQ fortwo", "#1", "#3"],
  dacia: ["Sandero", "Duster", "Logan", "Spring", "Jogger", "Lodgy"],
  porsche: ["911", "Macan", "Cayenne", "Panamera", "Taycan", "Boxster", "Cayman"],
  "land rover": ["Range Rover", "Range Rover Sport", "Range Rover Evoque", "Range Rover Velar", "Discovery", "Discovery Sport", "Defender"],
};

// Dataset professioni comuni in Italia (per autocomplete preventivi)
export const COMMON_PROFESSIONS = [
  "Impiegato", "Operaio", "Dirigente", "Quadro", "Funzionario pubblico",
  "Insegnante", "Professore universitario", "Educatore", "Ricercatore",
  "Medico", "Infermiere", "Farmacista", "Veterinario", "Fisioterapista", "Psicologo",
  "Avvocato", "Notaio", "Commercialista", "Consulente del lavoro", "Architetto", "Ingegnere", "Geometra",
  "Imprenditore", "Libero professionista", "Artigiano", "Commerciante",
  "Casalinga", "Studente", "Pensionato", "Disoccupato",
  "Agricoltore", "Allevatore", "Pescatore",
  "Autista", "Camionista", "Tassista", "Macchinista", "Pilota", "Marinaio",
  "Cuoco", "Cameriere", "Barista", "Pasticcere", "Panettiere", "Macellaio",
  "Idraulico", "Elettricista", "Muratore", "Falegname", "Meccanico", "Carrozziere",
  "Estetista", "Parrucchiere", "Sarto",
  "Giornalista", "Fotografo", "Grafico", "Designer", "Web designer", "Programmatore", "Sviluppatore software",
  "Forze dell'ordine", "Carabiniere", "Poliziotto", "Vigile del fuoco", "Militare",
  "Magistrato", "Bancario", "Assicuratore", "Agente immobiliare",
  "Attore", "Musicista", "Artista", "Sportivo professionista", "Allenatore",
];

/**
 * Input con autocomplete leggero (suggerimenti by prefix).
 * Props:
 *  - value, onChange: controlled
 *  - suggestions: array di stringhe (deduplicate, case-insensitive)
 *  - placeholder, testid, className
 */
export const VehicleAutocomplete = ({
  value,
  onChange,
  suggestions = [],
  placeholder = "",
  className = "",
  testid,
  maxItems = 8,
}) => {
  const [open, setOpen] = useState(false);
  const [focusIdx, setFocusIdx] = useState(-1);
  const containerRef = useRef(null);

  // Dedup case-insensitive e ordina alfabeticamente
  const deduped = useMemo(() => {
    const seen = new Map();
    for (const s of suggestions) {
      if (!s) continue;
      const k = String(s).trim();
      if (!k) continue;
      const lc = k.toLowerCase();
      if (!seen.has(lc)) seen.set(lc, k);
    }
    return Array.from(seen.values()).sort((a, b) => a.localeCompare(b, "it"));
  }, [suggestions]);

  const filtered = useMemo(() => {
    const q = (value || "").trim().toLowerCase();
    if (!q) return deduped.slice(0, maxItems);
    // Startswith first, then contains
    const starts = [];
    const contains = [];
    for (const s of deduped) {
      const lc = s.toLowerCase();
      if (lc === q) continue; // skip exact
      if (lc.startsWith(q)) starts.push(s);
      else if (lc.includes(q)) contains.push(s);
      if (starts.length + contains.length >= maxItems * 2) break;
    }
    return [...starts, ...contains].slice(0, maxItems);
  }, [value, deduped, maxItems]);

  useEffect(() => {
    const onDocClick = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  const pick = (s) => {
    onChange(s);
    setOpen(false);
    setFocusIdx(-1);
  };

  const onKeyDown = (e) => {
    if (!open || filtered.length === 0) {
      if (e.key === "ArrowDown") { setOpen(true); return; }
      return;
    }
    if (e.key === "ArrowDown") { e.preventDefault(); setFocusIdx((i) => Math.min(filtered.length - 1, i + 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setFocusIdx((i) => Math.max(0, i - 1)); }
    else if (e.key === "Enter" && focusIdx >= 0) { e.preventDefault(); pick(filtered[focusIdx]); }
    else if (e.key === "Escape") { setOpen(false); setFocusIdx(-1); }
  };

  return (
    <div ref={containerRef} className={`relative ${className}`}>
      <Input
        data-testid={testid}
        placeholder={placeholder}
        value={value || ""}
        onFocus={() => setOpen(true)}
        onChange={(e) => { onChange(e.target.value); setOpen(true); setFocusIdx(-1); }}
        onKeyDown={onKeyDown}
        autoComplete="off"
      />
      {open && filtered.length > 0 && (
        <div
          data-testid={testid ? `${testid}-suggestions` : undefined}
          className="absolute z-50 mt-1 left-0 right-0 bg-white border border-slate-200 rounded-md shadow-lg max-h-60 overflow-y-auto"
        >
          {filtered.map((s, i) => (
            <button
              type="button"
              key={s}
              onMouseDown={(e) => { e.preventDefault(); pick(s); }}
              onMouseEnter={() => setFocusIdx(i)}
              className={`w-full text-left px-3 py-1.5 text-sm transition-colors ${
                i === focusIdx ? "bg-blue-50 text-blue-900" : "hover:bg-slate-50 text-slate-700"
              }`}
            >
              {s}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};
